import { api } from '@goaltree/backend/convex/_generated/api'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { ChevronRight, ClipboardList, PencilLine, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AppSidebar } from '@/components/app-sidebar'
import { ProgressBar, StatusPill } from '@/components/coaching/coach-dashboard'
import { timeAgo } from '@/components/groves/grove-utils'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'
import { UserAvatar } from '@/components/user-avatar'
import { goalUrl } from '@/lib/utils'
import { useUserMode } from '@/store/user-mode.store'

export const Route = createFileRoute('/_authenticated/coaching/plans')({
  head: () => ({
    meta: [{ title: 'Client plans — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const clients = useQuery(api.coaching.clientPlans)
  const ago = (ms: number) => timeAgo(ms, i18n.language)

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader title={t('coachingPlans.title')} showInbox={false} />
        <SiteHeader className="hidden lg:flex" />
        <div className="flex-1 overflow-y-auto px-4 pb-16 lg:pb-10">
          <div className="mx-auto max-w-(--page-col) pt-6 lg:pt-10">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('coachingPlans.eyebrow')}
            </p>
            <h1 className="mt-1 text-2xl font-black tracking-tight">{t('coachingPlans.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('coachingPlans.desc')}</p>

            {clients === undefined ? (
              <div className="mt-6 space-y-5">
                {Array.from({ length: 2 }, (_, i) => (
                  <div key={i} className="h-36 animate-pulse rounded-2xl bg-muted/40" />
                ))}
              </div>
            ) : clients.length === 0 ? (
              <div className="mt-6 flex flex-col items-center gap-2 rounded-2xl border bg-card px-6 py-12 text-center">
                <ClipboardList className="size-8 text-muted-foreground" strokeWidth={1.75} />
                <p className="font-semibold">{t('coachingPlans.emptyTitle')}</p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {t('coachingPlans.emptyDesc')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    useUserMode.getState().requestInvite(true)
                    void navigate({ to: '/coaching' })
                  }}
                  className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-coach px-4 py-2 text-sm font-bold text-white hover:bg-coach/90"
                >
                  <UserPlus className="size-4" strokeWidth={2.25} />
                  {t('coachingMode.inviteClient')}
                </button>
              </div>
            ) : (
              <div className="mt-6 space-y-6">
                {clients.map((c) => (
                  <section key={c.linkId}>
                    <Link
                      to="/coaching/$clientId"
                      params={{ clientId: c.clientId }}
                      className="group mb-2 flex items-center gap-2.5"
                    >
                      <UserAvatar
                        image={c.client.image}
                        name={c.client.name}
                        size={28}
                        className="size-7 rounded-full"
                      />
                      <span className="min-w-0 truncate text-sm font-semibold group-hover:underline">
                        {c.client.name}
                      </span>
                      <StatusPill status={c.status} />
                      <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                    </Link>
                    <div className="overflow-hidden rounded-2xl border bg-card">
                      {c.goals.length === 0 && (
                        <p className="px-4 py-4 text-sm text-muted-foreground">
                          {t('coaching.noGoals')}
                        </p>
                      )}
                      {c.goals.map((g) => (
                        <Link
                          key={g._id}
                          to="/goals/$goalId"
                          params={{ goalId: goalUrl(g.title, g._id).replace('/goals/', '') }}
                          className="flex items-center gap-3 border-t px-4 py-3 transition-colors first:border-t-0 hover:bg-muted/40"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <p
                                className={cn(
                                  'min-w-0 flex-1 truncate text-sm font-semibold',
                                  g.done && 'text-muted-foreground line-through',
                                )}
                              >
                                {g.title}
                              </p>
                              <span className="font-mono text-xs text-muted-foreground">
                                {Math.round(g.progress)}%
                              </span>
                            </div>
                            <ProgressBar value={g.progress} className="mt-1.5" />
                            {g.editedByMe && g.lastEditedAt !== null && (
                              <p className="mt-1.5 flex items-center gap-1 font-mono text-[11px] text-primary-text/80">
                                <PencilLine className="size-3 shrink-0" />
                                {t('coachingPlans.editedByYou')} · {ago(g.lastEditedAt)}
                              </p>
                            )}
                            <p className="mt-1 truncate font-mono text-[11px] text-muted-foreground">
                              {t('coachingPlans.tasks', { done: g.tasksDone, total: g.tasksTotal })}
                              {' · '}
                              {t('coachingPlans.active', { time: ago(g.lastActivityAt) })}
                            </p>
                          </div>
                          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                        </Link>
                      ))}
                      {c.more > 0 && (
                        <Link
                          to="/coaching/$clientId"
                          params={{ clientId: c.clientId }}
                          className="block border-t px-4 py-2.5 text-xs font-semibold text-primary-text hover:bg-muted/40"
                        >
                          {t('coachingPlans.more', { count: c.more })}
                        </Link>
                      )}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
